// Flux de notifications temps réel.
//
// Le serveur parle SSE — une réponse HTTP qui ne se termine pas, où chaque
// évènement est une suite de lignes `data:` close par une ligne vide. On la lit
// avec fetch plutôt qu'avec EventSource, pour une raison précise : EventSource
// n'expose aucun moyen de poser un en-tête, et ne peut donc s'authentifier que
// par cookie. Or en développement le front tourne sur localhost pendant que
// l'API est sur tegeo.fr : le cookie `Token`, posé sur localhost, n'est jamais
// transmis. C'est ce que contournait l'interception de XMLHttpRequest du temps
// de SockJS.
//
// fetch permet les deux : l'en-tête Authorization quand le jeton est lisible,
// le cookie quand il ne l'est pas — iam le pose en HttpOnly.
//
// Ce que fetch ne fait pas, contrairement à EventSource, c'est reconnecter tout
// seul. D'où la boucle ci-dessous.

const DELAI_RECONNEXION = 5000

/**
 * Ouvre le flux et appelle `surMessage` pour chaque évènement reçu.
 *
 * @param {string} url          adresse du flux
 * @param {string} jeton        jeton d'accès, vide s'il n'est pas lisible
 * @param {function} surMessage appelée avec l'objet décodé
 * @returns {function} à appeler pour fermer le flux
 */
export function ouvrirFluxNotifications(url, jeton, surMessage) {
    const controle = new AbortController()
    let arrete = false
    let minuterie = null

    const options = {
        signal: controle.signal,
        headers: {Accept: "text/event-stream"},
    }
    if (jeton) {
        options.headers.Authorization = jeton
    } else {
        // Le jeton n'est pas lisible : il est dans un cookie HttpOnly, qu'il
        // faut autoriser à partir explicitement puisque l'API est sur un autre
        // sous-domaine.
        options.credentials = "include"
    }

    async function boucle() {
        while (!arrete) {
            try {
                const reponse = await fetch(url, options)
                if (!reponse.ok || !reponse.body) {
                    throw new Error(`flux refusé (${reponse.status})`)
                }
                await lire(reponse.body, surMessage, () => arrete)
            } catch (e) {
                if (arrete || e.name === "AbortError") {
                    return
                }
                console.debug("Flux de notifications interrompu", e.message)
            }
            if (arrete) {
                return
            }
            await new Promise(resolve => {
                minuterie = setTimeout(resolve, DELAI_RECONNEXION)
            })
        }
    }

    boucle()

    return () => {
        arrete = true
        clearTimeout(minuterie)
        controle.abort()
    }
}

// lire consomme le corps de la réponse et découpe les évènements SSE.
async function lire(corps, surMessage, estArrete) {
    const lecteur = corps.getReader()
    const decodeur = new TextDecoder()
    let tampon = ""

    while (!estArrete()) {
        const {done, value} = await lecteur.read()
        if (done) {
            return
        }
        tampon += decodeur.decode(value, {stream: true})

        // Un évènement se termine par une ligne vide. Le reste du tampon est un
        // évènement encore incomplet, qu'on garde pour le prochain morceau.
        let coupure
        while ((coupure = tampon.indexOf("\n\n")) !== -1) {
            const brut = tampon.slice(0, coupure)
            tampon = tampon.slice(coupure + 2)

            const donnees = brut
                .split("\n")
                // Les lignes de commentaire (battement de cœur) et les champs
                // autres que `data` ne portent rien à afficher.
                .filter(l => l.startsWith("data:"))
                .map(l => l.slice(5).trimStart())
                .join("\n")

            if (!donnees) {
                continue
            }
            try {
                surMessage(JSON.parse(donnees))
            } catch (e) {
                console.error("Notification illisible", e)
            }
        }
    }
}
